import { AppLandingPageTray, AppLandingSlot } from '../../../domain/app-landing-page';
import { UpdateMarketingAppLandingPageItemApiModel } from '@cxb/editor-stb-api';
import { LocalizedImageApiModel2, MarketingAppLandingPageItemApiModel } from '@cxb/editor-stb-api/model/models';
import { PageType } from '../../../page-edit-screen/page-type';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import { localizedDataMapper } from '../../mapping/create-tray-api-domain-common';
import { commonMapper, mapApiModelImageToDomain } from '../../mapping/create-tray-api-to-domain';
import { LocalizedDomainImages } from '../../../domain/localized-domain-images';
import { AppLandingSlotViewModel, AppLandingTrayViewModel } from './app-landing-mobile-tray-form-view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapDomainImageToViewModelImage,
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { AppLandingCotvTrayViewModel } from '../app-landing-cotv-tray-form/app-landing-cotv-tray-form-view-model';

// 'AppLandingPageTray'

/**
 * DOMAIN -> API
 */

export const mapAppLandingPageItemDomainModelToApiModel = (
  pageItem: AppLandingPageTray,
  pageVersionId: string,
  tenantId?: string
): UpdateMarketingAppLandingPageItemApiModel => {
  return {
    name: pageItem.internalName,
    headingLocalized: pageItem.registerHeading,
    descriptionLocalized: pageItem.registerDescription,
    titleLocalized: pageItem.registerTitle,
    userSegmentIds: pageItem.userSegmentIds,
    countryIds: pageItem.countryIds,
    slots: pageItem.slots.map((slot) => ({
      headingLocalized: slot.headlineText,
      subHeadingLocalized: slot.description,
      images: { enImageId: slot.backgroundImages.en[0].id, arImageId: slot.backgroundImages.ar[0].id },
    })),
    qrImageId: pageItem.qrImage?.id,
    tenantId,
    // pageVersionId, TODO:TB look after this
  };
};

/**
 * API -> DOMAIN
 */

const mapPageTypeFromPageTypeStringValue = (pageTypeStringValue: string): PageType => {
  if (pageTypeStringValue.toLowerCase().includes('mobile')) {
    return PageType.appLandingMobile;
  } else if (pageTypeStringValue.toLowerCase().includes('tablet')) {
    return PageType.appLandingTablet;
  } else if (pageTypeStringValue.toLowerCase().includes('cotv')) {
    return PageType.appLandingCotv;
  } else {
    return PageType.appLandingAppleAndroidTv;
  }
};

const mapBackgroundImageApiModelToDomainModel = (background: LocalizedImageApiModel2): LocalizedDomainImages => {
  return {
    en: background.en ? [mapApiModelImageToDomain(background.en)] : undefined,
    ar: background.ar ? [mapApiModelImageToDomain(background.ar)] : undefined,
  };
};

export const mapAppLandingPageItemApiModelToDomainModel = (
  pageItem: MarketingAppLandingPageItemApiModel,
  pageType: PageType
): AppLandingPageTray => {
  return remainderMapper(mapperPipe(pageItem, commonMapper), () => ({
    type: 'AppLandingPageTray',
    registerTitle: pageItem.titleLocalized ? localizedDataMapper(pageItem.titleLocalized) : undefined,
    registerDescription: pageItem.descriptionLocalized ? localizedDataMapper(pageItem.descriptionLocalized) : undefined,
    registerHeading: pageItem.headingLocalized ? localizedDataMapper(pageItem.headingLocalized) : undefined,
    qrImage: pageItem.qrImage ? mapApiModelImageToDomain(pageItem.qrImage) : undefined,
    internalName: pageItem.name,
    userSegmentIds: pageItem.userSegmentIds,
    countryIds: pageItem.countryIds,
    pageType: mapPageTypeFromPageTypeStringValue(pageType.stringValue),
    slots: pageItem.slots.map((slot) => ({
      id: slot.id,
      headlineText: localizedDataMapper(slot.headingLocalized),
      description: localizedDataMapper(slot.subHeadingLocalized),
      backgroundImages: mapBackgroundImageApiModelToDomainModel(slot.images),
    })),
  }));
};

/**
 * DOMAIN -> VIEWMODEL
 */

const mapAppLandingSlotDomainModelToViewModel = (slot: AppLandingSlot): AppLandingSlotViewModel => {
  return {
    id: slot.id,
    headlineText: slot.headlineText,
    description: slot.description,
    backgroundImages: mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(slot.backgroundImages),
  };
};

export const mapAppLandingPageTrayDomainModelToViewModel = (pageItem: AppLandingPageTray): AppLandingTrayViewModel => {
  const isMobileTemplate = pageItem.pageType === PageType.appLandingMobile || pageItem.pageType === PageType.appLandingTablet;
  const commonPart = {
    id: pageItem.id,
    internalName: pageItem.internalName,
    slots: pageItem.slots.map((slot) => mapAppLandingSlotDomainModelToViewModel(slot)),
    pageType: pageItem.pageType,
    userSegmentIds: pageItem.userSegmentIds,
    countryIds: pageItem.countryIds,
  };
  return isMobileTemplate
    ? {
        ...commonPart,
        type: TrayFormViewModelType.AppLandingMobileTray,
      }
    : {
        ...commonPart,
        type: TrayFormViewModelType.AppLandingCotvTray,
        registerDescription: pageItem.registerDescription,
        registerHeading: pageItem.registerHeading,
        registerTitle: pageItem.registerTitle,
        qrCodeImage: pageItem.qrImage ? mapDomainImageToViewModelImage(pageItem.qrImage) : undefined,
      };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapAppLandingTrayViewModelToDomainModel = (
  appLanding: AppLandingTrayViewModel,
  platformIds: Record<PlatformType, string>
): AppLandingPageTray => {
  const commonPart = {
    id: appLanding.id,
    pageType: appLanding.pageType,
    order: undefined,
    visible: undefined,
    draggable: true,
    userSegmentIds: appLanding.userSegmentIds,
    countryIds: appLanding.countryIds,
    internalName: appLanding.internalName,
    slots: appLanding.slots.map((slot) => ({
      id: slot.id,
      headlineText: slot.headlineText,
      description: slot.description,
      backgroundImages: { en: [slot.backgroundImages.WEB.en], ar: [slot.backgroundImages.WEB.ar] },
    })),
    platformIds: Object.values(platformIds),
  };
  return appLanding.type === TrayFormViewModelType.AppLandingMobileTray
    ? {
        ...commonPart,
        type: 'AppLandingPageTray',
      }
    : {
        ...commonPart,
        qrImage: (appLanding as AppLandingCotvTrayViewModel).qrCodeImage,
        registerHeading: (appLanding as AppLandingCotvTrayViewModel).registerHeading,
        registerDescription: (appLanding as AppLandingCotvTrayViewModel).registerDescription,
        registerTitle: (appLanding as AppLandingCotvTrayViewModel).registerTitle,
        type: 'AppLandingPageTray',
      };
};
